import { isOrderBrindeLine, orderSubtotal, readOrderCampaigns } from './cart-brinde-pedido.js';

const NONE = Object.freeze({ state: 'none', campaign: null, remaining: 0, ratio: 0 });
const TITLE_TOKENS = /(\{gift\}|\{amount\})/;
export const CELEBRATION_MS = 760;

const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);

export function promoGiftProgress(state) {
  if (!isRecord(state)) return { ...NONE };
  const campaigns = readOrderCampaigns(state.orderCampaigns);
  const [campaign] = campaigns;
  if (!campaign) return { ...NONE };
  const lines = Array.isArray(state.lines) ? state.lines.filter(isRecord) : [];
  const subtotal = orderSubtotal(campaign, lines);
  const remaining = Math.max(0, campaign.minimum - subtotal);
  const hasGift = lines.some((line) => isOrderBrindeLine(line, campaigns) && line.brinde === campaign.id);
  let barState = 'progress';
  if (remaining === 0) barState = hasGift ? 'won' : 'reached';
  return {
    state: barState,
    campaign: campaign.id,
    remaining,
    ratio: Math.min(1, subtotal / campaign.minimum),
  };
}

const moneyText = (cents) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
}).format(cents / 100);

function readProgress(drawer) {
  const node = drawer.querySelector('[data-cart-brinde-state]');
  if (!node) return { text: null, progress: { ...NONE } };
  try {
    return { text: node.textContent, progress: promoGiftProgress(JSON.parse(node.textContent)) };
  } catch {
    return { text: node.textContent, progress: { ...NONE } };
  }
}

export function createPromoGiftBar({ root, now = () => Date.now() }) {
  const drawer = root.getElementById('cart-drawer');
  if (!drawer) return null;
  let lastSeen = null;
  let current = { ...NONE };
  let celebration = null;
  let heldForStateText = null;

  const textNode = (tagName, text, className) => {
    const node = root.createElement(tagName);
    if (className) node.className = className;
    node.textContent = text;
    return node;
  };

  function titleNodes(template, { gift, amount }) {
    return template.split(TITLE_TOKENS).filter(Boolean).map((piece) => {
      if (piece === '{gift}') return textNode('span', gift, 'cart-promo-gift__gift');
      if (piece === '{amount}') return textNode('strong', amount);
      return textNode('span', piece);
    });
  }

  function writeTitle(block, progress) {
    const title = block.querySelector('[data-promo-gift-title]');
    if (!title) return;
    const template = progress.state === 'won' ? block.dataset.textWon : block.dataset.textProgress;
    const values = { gift: block.dataset.giftName || '', amount: moneyText(progress.remaining) };
    title.replaceChildren(...titleNodes(template || '', values));
  }

  const setFill = (block, ratio) => {
    const fill = block.querySelector('[data-promo-gift-fill]');
    if (fill) fill.setAttribute('style', `--promo-gift-progress: ${Number((ratio * 100).toFixed(3))}%`);
    return fill;
  };

  function paint(block, progress) {
    if (progress.state === 'none') {
      block.setAttribute('hidden', '');
      return;
    }
    block.removeAttribute('hidden');
    block.dataset.state = progress.state;
    block.dataset.campaign = progress.campaign;
    writeTitle(block, progress);
    const bar = block.querySelector('[role="progressbar"]');
    if (bar) bar.setAttribute('aria-valuenow', String(Math.floor(progress.ratio * 100)));
    setFill(block, progress.ratio);
  }

  function growFillFrom(block, fromRatio) {
    const fill = setFill(block, fromRatio);
    if (fill) flushLayout(fill);
    block.classList.add('is-moving');
    setFill(block, current.ratio);
  }

  function resumeCelebration(block, elapsed) {
    if (elapsed > 0) block.setAttribute('style', `--promo-gift-elapsed: ${Math.round(elapsed)}ms`);
    growFillFrom(block, celebration.fromRatio);
    block.classList.add('is-celebrating');
  }

  function reveal(block) {
    const from = lastSeen;
    lastSeen = current;
    if (!from || from.state === 'none' || current.state !== 'won') celebration = null;
    if (!from || from.state === 'none' || current.state === 'none') return;
    if (from.state === 'progress' && current.state === 'won') celebration = { fromRatio: from.ratio, startedAt: now() };
    const elapsed = celebration ? now() - celebration.startedAt : CELEBRATION_MS;
    if (elapsed >= CELEBRATION_MS) {
      celebration = null;
      growFillFrom(block, from.ratio);
      return;
    }
    resumeCelebration(block, elapsed);
  }

  const blockOf = () => drawer.querySelector('[data-cart-promo-gift]');

  function holdOrHideReached({ text, progress }) {
    if (progress.state !== 'reached') {
      heldForStateText = null;
      return progress;
    }
    const hold = current.state === 'progress' && (heldForStateText === null || heldForStateText === text);
    heldForStateText = hold ? text : null;
    return hold ? current : { ...NONE };
  }

  function render() {
    const block = blockOf();
    current = holdOrHideReached(readProgress(drawer));
    if (!block) {
      if (drawer.open) lastSeen = current;
      return;
    }
    paint(block, current);
    if (drawer.open) reveal(block);
  }

  drawer.addEventListener('cart:rendered', render);
  drawer.addEventListener('cart:opened', () => {
    const block = blockOf();
    if (block) reveal(block);
  });
  render();
  if (!lastSeen) lastSeen = current;

  return { render };
}

function flushLayout(node) {
  return node.offsetWidth;
}

if (typeof document !== 'undefined') {
  const start = () => createPromoGiftBar({ root: document, now: () => performance.now() });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}
